import { useEffect, useMemo, useState } from "react";
import DashboardLayout from "../Layouts/DashboardLayout";
import { useAuth } from "../Context/AuthContext";

const API = "/api";
const money = (value) => `₹${Number(value || 0).toFixed(2)}`;

export default function Payments() {
  const { user } = useAuth();
  const isSuperAdmin = user?.role === "super_admin";
  const [payments, setPayments] = useState([]);
  const [branches, setBranches] = useState([]);
  const [filters, setFilters] = useState({
    search: "",
    mode: "",
    branch_id: "",
  });
  const [page, setPage] = useState(1);
  const perPage = 10;

  useEffect(() => {
    load();
  }, []);
  useEffect(() => {
    setPage(1);
  }, [filters]);
  const load = async () => {
    try {
      const [p, b] = await Promise.all([
        fetch(`${API}/sales-payments/`, { credentials: "include" }),
        fetch(`${API}/branches`, { credentials: "include" }),
      ]);
      const pd = await p.json(),
        bd = await b.json();
      if (pd.status !== "success") throw new Error(pd.message);
      setPayments(pd.data);
      if (bd.status === "success") setBranches(bd.data);
    } catch (e) {
      alert(e.message || "Failed to load payments");
    }
  };
  const filtered = useMemo(
    () =>
      payments.filter((p) => {
        const text =
          `${p.customer} ${p.order_no} ${p.invoice_no} ${p.customer_gst_no || ""}`.toLowerCase();
        return (
          (!filters.search || text.includes(filters.search.toLowerCase())) &&
          (!filters.mode || p.payment_mode === filters.mode) &&
          (!filters.branch_id || String(p.branch_id) === filters.branch_id)
        );
      }),
    [payments, filters],
  );
  const pages = Math.max(1, Math.ceil(filtered.length / perPage));
  const records = filtered.slice((page - 1) * perPage, page * perPage);
  const paid = filtered.reduce((sum, p) => sum + Number(p.amount), 0);
  return (
    <DashboardLayout>
      <div className="space-y-6">
        <header>
          <h1 className="text-3xl font-serif">Sales Payments</h1>
          <p className="mt-1 text-sm text-gray-400">
            Each payment is linked to one sales order and its invoice.
          </p>
        </header>
        <section className="grid gap-4 md:grid-cols-3">
          <Card label="Payments shown" value={filtered.length} />
          <Card
            label="Amount received"
            value={money(paid)}
            accent="text-emerald-400"
          />
          <Card
            label="Invoices represented"
            value={new Set(filtered.map((p) => p.invoice_no)).size}
            accent="text-amber-300"
          />
        </section>
        <section className="grid gap-3 rounded-2xl border border-white/10 bg-white/5 p-4 md:grid-cols-3">
          <input
            value={filters.search}
            onChange={(e) => setFilters({ ...filters, search: e.target.value })}
            placeholder="Customer, order, invoice or GSTIN"
            className="rounded-lg border border-white/10 bg-black/30 px-3 py-2"
          />
          <select
            value={filters.mode}
            onChange={(e) => setFilters({ ...filters, mode: e.target.value })}
            className="rounded-lg border border-white/10 bg-black/30 px-3 py-2"
          >
            <option value="">All payment modes</option>
            {["Cash", "UPI", "Card", "Bank Transfer"].map((m) => (
              <option key={m}>{m}</option>
            ))}
          </select>
          {isSuperAdmin && (
            <select
              value={filters.branch_id}
              onChange={(e) =>
                setFilters({ ...filters, branch_id: e.target.value })
              }
              className="rounded-lg border border-white/10 bg-black/30 px-3 py-2"
            >
              <option value="">All branches</option>
              {branches.map((b) => (
                <option key={b.id} value={b.id}>
                  {b.name}
                </option>
              ))}
            </select>
          )}
        </section>
        <div className="overflow-x-auto rounded-2xl border border-white/10 bg-[#0b0b0f]">
          <table className="w-full min-w-[1200px] text-sm">
            <thead className="bg-black/40 text-left text-amber-400">
              <tr>
                {[
                  "Date",
                  "Invoice / order",
                  "Customer & GSTIN",
                  "Branch",
                  "Mode",
                  "Payment",
                  "Taxable / GST",
                  "Invoice total",
                  "Paid / balance",
                  "Received by",
                ].map((h) => (
                  <th key={h} className="p-4">
                    {h}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {records.length === 0 ? (
                <tr>
                  <td colSpan="10" className="p-10 text-center text-gray-500">
                    No payments match these filters.
                  </td>
                </tr>
              ) : (
                records.map((p) => (
                  <tr
                    key={p.id}
                    className="border-t border-white/10 hover:bg-white/5"
                  >
                    <td className="p-4">
                      {new Date(p.date).toLocaleDateString("en-IN")}
                    </td>
                    <td className="p-4">
                      <div className="font-semibold text-amber-300">
                        {p.invoice_no}
                      </div>
                      <div className="text-xs text-gray-400">{p.order_no}</div>
                    </td>
                    <td className="p-4">
                      <div>{p.customer}</div>
                      <div className="text-xs text-gray-400">
                        GSTIN: {p.customer_gst_no || "Not provided"}
                      </div>
                    </td>
                    <td className="p-4">{p.branch || "-"}</td>
                    <td className="p-4">{p.payment_mode}</td>
                    <td className="p-4 font-semibold text-emerald-400">
                      {money(p.amount)}
                    </td>
                    <td className="p-4">
                      <div>{money(p.subtotal - p.discount)}</div>
                      <div className="text-xs text-amber-300">
                        GST {money(p.gst_amount)}
                      </div>
                    </td>
                    <td className="p-4">{money(p.grand_total)}</td>
                    <td className="p-4">
                      <div className="text-emerald-400">
                        {money(p.paid_amount)}
                      </div>
                      <div className="text-xs text-red-300">
                        Due {money(p.balance_amount)}
                      </div>
                    </td>
                    <td className="p-4">{p.received_by || "-"}</td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
        <footer className="flex items-center justify-between text-sm text-gray-400">
          <span>
            Showing {filtered.length ? (page - 1) * perPage + 1 : 0}–
            {Math.min(page * perPage, filtered.length)} of {filtered.length}
          </span>
          <div className="flex gap-2">
            <button
              disabled={page === 1}
              onClick={() => setPage(page - 1)}
              className="rounded-lg border border-white/15 px-3 py-1.5 disabled:opacity-40"
            >
              Previous
            </button>
            <span className="px-2 py-1.5">
              Page {page} of {pages}
            </span>
            <button
              disabled={page === pages}
              onClick={() => setPage(page + 1)}
              className="rounded-lg border border-white/15 px-3 py-1.5 disabled:opacity-40"
            >
              Next
            </button>
          </div>
        </footer>
      </div>
    </DashboardLayout>
  );
}
function Card({ label, value, accent = "text-white" }) {
  return (
    <div className="rounded-2xl border border-white/10 bg-white/5 p-5">
      <div className="text-sm text-gray-400">{label}</div>
      <div className={`mt-2 text-2xl font-semibold ${accent}`}>{value}</div>
    </div>
  );
}
